import i18n from '../../i18n';
import { FACEBOOK_LIMITS, INSTAGRAM_ONLY, POST_LIMITS, type PostTargets } from './limits';

/**
 * Gets a file into the shape Instagram accepts before it is uploaded (D-092).
 * Instagram takes JPEG photos only, between 4:5 and 1.91:1, up to 8 MB; the
 * server stores bytes as they are, so conversion happens here, where the
 * browser can decode PNG, WebP and (on Apple devices) HEIC for free.
 *
 * Photos for the feed are centre-cropped into the accepted ratio range and
 * scaled to at most 1440 px wide; a Reel cover and a story photo are only
 * converted (a story takes any ratio, D-136). Videos are passed through after
 * a duration and size check — transcoding is out of scope.
 *
 * A post for a Facebook Page (D-143) is not cropped unless it also goes to
 * Instagram; whenever it reaches a Page, photos stay under 4 MB and a Reel
 * under 90 seconds.
 */
export type MediaPurpose = 'feed' | 'reel' | 'cover' | 'story';

export interface PreparedFile {
  blob: Blob;
  filename: string;
  /** What was changed, to tell the user; null when the file went up as is. */
  note: string | null;
}

const MAX_WIDTH = 1440;
const VIDEO_TYPES = ['video/mp4', 'video/quicktime'];
const REEL_SECONDS = { min: 3, max: 15 * 60 };
/** Carousel videos and story videos (D-136) share Meta's 3–60 s window. */
const SHORT_VIDEO_SECONDS = { min: 3, max: 60 };

export async function prepareMedia(file: File, purpose: MediaPurpose, targets: PostTargets = INSTAGRAM_ONLY): Promise<PreparedFile> {
  if (file.type.startsWith('video/')) return prepareVideo(file, purpose, targets);
  if (file.type.startsWith('image/')) return prepareImage(file, purpose, targets);
  throw new Error(i18n.t('composerMedia.prepare.unknown'));
}

async function prepareVideo(file: File, purpose: MediaPurpose, targets: PostTargets): Promise<PreparedFile> {
  if (purpose === 'cover') throw new Error(i18n.t('composerMedia.prepare.coverPhoto'));
  if (!VIDEO_TYPES.includes(file.type)) throw new Error(i18n.t('composerMedia.prepare.videoType'));
  if (purpose === 'story' && file.size > POST_LIMITS.maxStoryVideoBytes) throw new Error(i18n.t('composerMedia.prepare.storySize'));
  if (file.size > POST_LIMITS.maxVideoBytes) throw new Error(i18n.t('composerMedia.prepare.videoSize'));
  const seconds = await videoDuration(file);
  const reel = targets.facebook ? { min: REEL_SECONDS.min, max: FACEBOOK_LIMITS.reelMaxSeconds } : REEL_SECONDS;
  const range = purpose === 'reel' ? reel : SHORT_VIDEO_SECONDS;
  if (seconds !== null && (seconds < range.min || seconds > range.max)) {
    throw new Error(
      i18n.t(
        purpose === 'reel'
          ? targets.facebook
            ? 'composerMedia.prepare.reelPage'
            : 'composerMedia.prepare.reel'
          : purpose === 'story'
            ? 'composerMedia.prepare.storyLength'
            : 'composerMedia.prepare.carouselLength',
      ),
    );
  }
  return { blob: file, filename: file.name, note: null };
}

function videoDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    const done = (value: number | null) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };
    video.preload = 'metadata';
    video.onloadedmetadata = () => done(Number.isFinite(video.duration) ? video.duration : null);
    video.onerror = () => done(null);
    video.src = url;
  });
}

async function prepareImage(file: File, purpose: MediaPurpose, targets: PostTargets): Promise<PreparedFile> {
  const image = await decode(file);
  const { width, height } = image;
  let sx = 0;
  let sy = 0;
  let sw = width;
  let sh = height;
  let cropped: string | null = null;
  if (purpose === 'feed' && targets.instagram) {
    const ratio = width / height;
    if (ratio < POST_LIMITS.minAspect) {
      sh = Math.round(width / POST_LIMITS.minAspect);
      sy = Math.round((height - sh) / 2);
      cropped = i18n.t('composerMedia.prepare.croppedPortrait');
    } else if (ratio > POST_LIMITS.maxAspect) {
      sw = Math.round(height * POST_LIMITS.maxAspect);
      sx = Math.round((width - sw) / 2);
      cropped = i18n.t('composerMedia.prepare.croppedLandscape');
    }
  }
  const scale = Math.min(1, MAX_WIDTH / sw);
  const isJpeg = file.type === 'image/jpeg';
  // Always re-encoded, JPEGs included: the decoder applies EXIF rotation, so the
  // upload's pixel dimensions are the ones people see — the server measures
  // pixels, and a sideways phone photo would otherwise fail the ratio check.

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(sw * scale);
  canvas.height = Math.round(sh * scale);
  const context = canvas.getContext('2d');
  if (!context) throw new Error(i18n.t('composerMedia.prepare.noCanvas'));
  context.fillStyle = '#ffffff'; // transparent PNG areas become white, not black
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

  const maxBytes = targets.facebook ? FACEBOOK_LIMITS.maxImageBytes : POST_LIMITS.maxImageBytes;
  let blob: Blob | null = null;
  for (const quality of [0.9, 0.82, 0.72]) {
    blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (blob && blob.size <= maxBytes) break;
  }
  if (!blob || blob.size > maxBytes) throw new Error(i18n.t('composerMedia.prepare.tooLarge'));

  const note = cropped ?? (!isJpeg ? i18n.t('composerMedia.prepare.converted') : scale < 1 ? i18n.t('composerMedia.prepare.resized') : null);
  return { blob, filename: file.name.replace(/\.[^.]+$/, '') + '.jpg', note };
}

async function decode(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      // fall through to <img>, which decodes more formats in some browsers
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(i18n.t('composerMedia.prepare.cannotOpen')));
    };
    img.src = url;
  });
}
